import { ArrowUpRight, Check, Copy, Eye } from 'lucide-react'
import { useState } from 'react'

import { estadoDelEnlace, rutaCorta, type EnlaceEnLaWeb, useEnlacesEnLaWeb } from '../api/enlaces'
import { abrirPrevia, type AlcanceDePrevia, COLECCIONES_CON_PREVIA } from '../api/previas'
import { useInterruptores } from '../api/interruptores'
import { Boton, clases } from '../componentes/ui'

/**
 * «Ver en la web ↗» (CLAUDE.md §20), como el enlace permanente de WordPress: en el listado,
 * un icono junto al título de cada fila que se ve en la web; en el editor, la dirección
 * completa bajo el título y un botón. Siempre en una pestaña nueva: el panel no se pierde.
 *
 * La dirección la da el núcleo (`api/enlaces.ts`); aquí solo se pinta.
 */

/** El icono de una fila: un enlace de verdad, fuera del enlace del título (dos `<a>` no se anidan). */
export const IconoVerEnLaWeb = ({ url, titulo }: { titulo: string; url: string }) => (
  <a
    aria-label={`Ver «${titulo}» en la web (se abre en una pestaña nueva)`}
    className="ml-1.5 inline-flex size-6 shrink-0 items-center justify-center rounded-boton align-middle text-tenue transition-colors hover:bg-papel-3 hover:text-violeta"
    href={url}
    rel="noopener"
    target="_blank"
    title={`Ver en la web: ${url}`}
  >
    <ArrowUpRight aria-hidden className="size-4" />
  </a>
)

type DelDocumento = { coleccion: string; sitio: number; id: number; borradores: boolean; estado: string; sucio: boolean }

const useEstado = (p: DelDocumento) => {
  const { data, isPending, isError } = useEnlacesEnLaWeb(p.coleccion, p.sitio, [p.id])
  // Si el núcleo no contesta, no se enseña nada: mejor sin enlace que con uno inventado.
  return estadoDelEnlace({ enlace: isError ? undefined : data?.enlaces[String(p.id)], cargando: isPending && !isError, borradores: p.borradores, estado: p.estado, sucio: p.sucio })
}

/** Bajo el título del editor: la dirección completa, o por qué todavía no tiene. */
export const DireccionEnLaWeb = (p: DelDocumento) => {
  const estado = useEstado(p)
  if (estado.tipo === 'cargando') return <p className="mt-1.5 h-5 w-64 max-w-full animate-pulse rounded bg-papel-3" />
  if (estado.tipo === 'nada') return null
  if (estado.tipo === 'sin-enlace') return <p className="mt-1.5 text-[13px] text-tenue">{estado.texto}</p>
  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-tenue">
      <span>Dirección:</span>
      <a className="min-w-0 break-all text-violeta underline-offset-2 hover:underline" href={estado.url} rel="noopener" target="_blank">
        {estado.url}
      </a>
      <BotonCopiar url={estado.url} />
      {estado.nota && <span className="text-tinta-2">· {estado.nota}</span>}
    </p>
  )
}

/** El botón de la cabecera del editor: solo cuando hay dónde ir. */
export const BotonVerEnLaWeb = (p: DelDocumento) => {
  const estado = useEstado(p)
  if (estado.tipo !== 'enlace') return null
  return (
    <a
      className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-boton border border-linea bg-papel px-3 text-[13px] font-medium whitespace-nowrap text-tinta shadow-suave transition-colors hover:border-linea-fuerte hover:bg-papel-2"
      href={estado.url}
      rel="noopener"
      target="_blank"
      title={estado.nota ? `${estado.url} — ${estado.nota}` : estado.url}
    >
      Ver en la web
      <ArrowUpRight aria-hidden className="size-4" />
    </a>
  )
}

/** Copiar una dirección: un icono que dice «Copiada» un momento. Si el navegador no deja, no hace nada. */
export const BotonCopiar = ({ url }: { url: string }) => {
  const [copiada, setCopiada] = useState(false)
  const copiar = () => {
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopiada(true)
        window.setTimeout(() => setCopiada(false), 1500)
      })
      .catch(() => undefined)
  }
  const etiqueta = copiada ? 'Dirección copiada' : 'Copiar la dirección'
  return (
    <button
      aria-label={etiqueta}
      className="inline-flex size-6 shrink-0 items-center justify-center rounded-boton align-middle text-tenue transition-colors hover:bg-papel-3 hover:text-violeta"
      onClick={copiar}
      title={etiqueta}
      type="button"
    >
      {copiada ? <Check aria-hidden className="size-3.5 text-ok" /> : <Copy aria-hidden className="size-3.5" />}
    </button>
  )
}

/**
 * «Vista previa»: pide al núcleo una dirección de previa de lo guardado sin publicar y la abre
 * en una pestaña nueva (plan del CMS dinámico §15). Con cambios sin guardar no se ofrece: la
 * previa enseñaría lo de antes.
 */
export const BotonVistaPrevia = ({ sitio, que, sucio = false, pequeno = true, texto = 'Vista previa' }: { pequeno?: boolean; que: AlcanceDePrevia; sitio: number; sucio?: boolean; texto?: string }) => {
  const [abriendo, setAbriendo] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  // Con la previa de borradores apagada en la instalación (el núcleo lo dice), no hay botón: daría 404.
  const { previaContenido } = useInterruptores()
  if (!previaContenido) return null
  const abrir = async () => {
    setAbriendo(true)
    setFallo(null)
    setFallo(await abrirPrevia(sitio, que))
    setAbriendo(false)
  }
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Boton
        cargando={abriendo}
        disabled={sucio}
        icono={<Eye />}
        onClick={() => void abrir()}
        pequeno={pequeno}
        title={sucio ? 'Guarda primero: la vista previa enseña lo guardado.' : 'Se abre en una pestaña nueva, con lo guardado sin publicar. El enlace caduca en unos minutos.'}
      >
        {texto}
      </Boton>
      {fallo && (
        <span className="max-w-72 text-right text-xs text-error" role="alert">
          {fallo}
        </span>
      )}
    </span>
  )
}

/**
 * La celda «Dirección» de un listado: la dirección corta («/propiedades/depa-x») con abrir y
 * copiar; sin dirección, la vista previa si es un borrador que el núcleo previsualiza, o por qué
 * no tiene, en gris. Lo que quien mira no puede leer no enseña nada.
 */
export const CeldaDeDireccion = ({ enlace, coleccion, sitio, id, borrador }: { borrador: boolean; coleccion: string; enlace: EnlaceEnLaWeb | undefined; id: number; sitio: null | number }) => {
  const { previaContenido } = useInterruptores()
  if (!enlace) return <span className="text-tenue">—</span>
  if (enlace.url) {
    return (
      <span className="inline-flex max-w-72 items-center gap-0.5">
        <a className="min-w-0 truncate text-tinta-2 underline-offset-2 hover:text-violeta hover:underline" href={enlace.url} rel="noopener" target="_blank" title={`Abrir en la web: ${enlace.url}`}>
          {rutaCorta(enlace.url)}
        </a>
        <BotonCopiar url={enlace.url} />
      </span>
    )
  }
  if (borrador && sitio !== null && COLECCIONES_CON_PREVIA.has(coleccion) && previaContenido) {
    return <BotonVistaPrevia que={{ alcance: 'documento', documento: { coleccion, id } }} sitio={sitio} />
  }
  return enlace.motivo ? <span className={clases('block max-w-64 text-xs text-tenue')}>{enlace.motivo}</span> : <span className="text-tenue">—</span>
}
